import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../api/auth';
import { chatAPI } from '../api/chat';
import MiraLinkLogo from '../components/MiraLinkLogo';
import OfficialBadge from '../components/OfficialBadge';

const Messenger = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [userData, convData] = await Promise.all([authAPI.getMe(token), chatAPI.getConversations(token)]);

      setUser(userData);
      setConversations(convData);
    } catch (err) {
      console.error('Failed to load data:', err);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const goToCreateChannel = () => {
    navigate('/channels/create');
    setTimeout(() => {
      if (!window.location.href.includes('channels/create')) {
        window.location.assign(`${window.location.origin}/#/channels/create`);
      }
    }, 0);
  };

  const handleLogout = () => {
    if (window.confirm('Выйти из аккаунта?')) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60_000) return 'только что';
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} мин`;
    if (diff < 86_400_000) {
      return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="messenger-page">
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center justify-between">
          <button onClick={handleRefresh} disabled={refreshing} className={`flex items-center gap-3 transition-opacity ${refreshing ? 'opacity-50' : 'hover:opacity-80'}`}>
            <MiraLinkLogo size={40} />
            <div className="text-left">
              <h1 className="text-xl font-bold" style={{ color: '#6B5CE7' }}>
                MiraLink
              </h1>
              <p className="text-xs text-gray-500 font-semibold tracking-wider">{refreshing ? 'Обновление...' : 'CONNECT. CREATE. SHARE.'}</p>
            </div>
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={goToCreateChannel}
              className="w-10 h-10 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white text-2xl hover:opacity-90 transition shadow-lg"
              title="Создать канал"
            >
              +
            </button>

            <div className="text-right">
              <div className="font-semibold text-gray-900 text-sm flex items-center gap-2" data-testid="user-username">
                <span>@{user?.username}</span>
                <OfficialBadge username={user?.username} />
              </div>
            </div>
          </div>
        </div>

        <div className="px-4 pb-3">
          <div onClick={() => navigate('/search')} className="bg-gray-100 rounded-full px-4 py-2 flex items-center gap-2 cursor-pointer hover:bg-gray-200 transition">
            <svg className="w-5 h-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-gray-500">Поиск</span>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto bg-white">
        {conversations.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-6xl mb-4">💬</div>
            <p className="text-gray-600 text-lg mb-4">Пока нет чатов</p>
            <button onClick={() => navigate('/search')} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-full hover:opacity-90 transition font-semibold">
              Найти пользователей
            </button>
          </div>
        ) : (
          <div>
            {conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => navigate(`/chat/${conv.id}`)}
                className="border-b border-gray-100 p-4 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition active:bg-gray-100"
                data-testid={`conversation-${conv.id}`}
              >
                {conv.other_user.avatar ? (
                  <img src={conv.other_user.avatar} alt={conv.other_user.username} className="w-14 h-14 rounded-full object-cover flex-shrink-0" />
                ) : (
                  <div className="w-14 h-14 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                    {conv.other_user.username.charAt(0).toUpperCase()}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <div className="font-semibold text-gray-900 truncate flex items-center gap-2">
                      <span>{conv.other_user.nickname || conv.other_user.username}</span>
                      <OfficialBadge username={conv.other_user.username} />
                    </div>
                    <span className="text-xs text-gray-500 flex-shrink-0 ml-2">{formatTime(conv.last_message_time)}</span>
                  </div>
                  {conv.last_message && <p className="text-gray-500 text-sm truncate">{conv.last_message}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border-t border-gray-200 px-4 py-2 flex justify-around items-center">
        <button onClick={() => navigate('/search')} className="flex flex-col items-center gap-1 text-gray-500 hover:text-purple-600 transition py-2">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-xs font-semibold">Контакты</span>
        </button>

        <button onClick={() => navigate('/messenger')} className="flex flex-col items-center gap-1 text-purple-600 py-2">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path d="M2 5a2 2 0 012-2h7a2 2 0 012 2v4a2 2 0 01-2 2H9l-3 3v-3H4a2 2 0 01-2-2V5z" />
            <path d="M15 7v2a4 4 0 01-4 4H9.828l-1.766 1.767c.28.149.599.233.938.233h2l3 3v-3h2a2 2 0 002-2V9a2 2 0 00-2-2h-1z" />
          </svg>
          <span className="text-xs font-bold">Чаты</span>
        </button>

        <button onClick={() => navigate('/edit-profile')} className="flex flex-col items-center gap-1 text-gray-500 hover:text-purple-600 transition py-2">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
          </svg>
          <span className="text-xs font-semibold">Профиль</span>
        </button>

        <button onClick={handleLogout} className="flex flex-col items-center gap-1 text-gray-500 hover:text-red-600 transition py-2">
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
              clipRule="evenodd"
            />
          </svg>
          <span className="text-xs font-semibold">Выйти</span>
        </button>
      </div>
    </div>
  );
};

export default Messenger;
